// src/legal/LegalGate.tsx
//
// Decides once, when a session starts, what may be rendered: the app, the app
// with the legal dialog on top, or the dialog alone. A session is one page
// load. After that the decision stands: a running session is never
// interrupted, whatever happens to the user record in the meantime. The one
// exception is the server refusing a request with `legal_required`.
//
// This file is all that a user with nothing outstanding pays for. The dialog,
// the Markdown renderer, the PDF builder and the texts are behind the lazy
// import below, and nothing here reads Firestore.
import {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useGetIdentity } from 'react-admin';
import { ErrorBoundary } from 'react-error-boundary';
import { rereadIdentity } from './identityCache';
import { onLegalRequired, reportIfLegalRequired } from './legalRequired';
import { gateState, type GateState, type LegalState } from './legalState';
import { LegalWaiting } from './LegalWaiting';

const LegalFlow = lazy(() => import('./LegalFlow'));

// One refused page load is many refused requests; one re-read answers them all.
const REREAD_INTERVAL_MS = 10_000;

interface CacheEvent {
    type: string;
    action?: { type: string; error?: unknown };
}

/** What a session was started with, and for whom. */
interface Decision {
    userId: string;
    state: GateState;
    /** The server has refused a request of this user in this session. */
    refused: boolean;
}

export const LegalGate = ({ children }: { children: ReactNode }) => {
    const { identity, isPending } = useGetIdentity();
    const queryClient = useQueryClient();
    const legal = identity?.legal as LegalState | undefined;

    const userId = identity ? String(identity.id) : null;

    // What this session was started with: decided from the first user record
    // that arrives, and from then on only changed by what the dialog reports
    // (the user's own decisions) or by a refusal of the server. Not by the
    // user record: it is read again every five minutes, the server can change
    // it at any time, and a deadline in it can pass while someone works.
    //
    // The decision belongs to the user it was made for. Another user in the
    // same tab is decided afresh.
    const [decision, setDecision] = useState<Decision | null>(null);
    let decided = decision?.userId === userId ? decision : null;
    if (decided === null && userId !== null) {
        decided = {
            userId,
            state: gateState(legal, Date.now()),
            refused: false,
        };
        setDecision(decided);
    }
    // Without a user record there is nothing to decide from. Once the query
    // has settled the pages are rendered all the same: RequireAccess denies
    // every one of them and /no-access offers the sign-out. But nothing is
    // decided by that. A record that arrives later is decided like any other;
    // deciding "let in" here would hold for the whole session.
    const state: GateState | null =
        decided?.state ?? (userId === null && !isPending ? 'ok' : null);
    const refused = decided?.refused ?? false;

    // The dialog reports what the session goes on with.
    const onState = useCallback(
        (next: GateState) =>
            setDecision(current =>
                current?.userId === userId
                    ? { ...current, state: next }
                    : current
            ),
        [userId]
    );

    // After `valid_until` the server refuses requests with `legal_required`,
    // also for a session that started long before. Then, and only then, the
    // user record is read again and the dialog comes up.
    const lastReread = useRef(Number.NEGATIVE_INFINITY);
    useEffect(() => {
        let mounted = true;
        const offRefusal = onLegalRequired(() => {
            const now = Date.now();
            if (now - lastReread.current < REREAD_INTERVAL_MS) return;
            lastReread.current = now;
            rereadIdentity(queryClient)
                .then(user => {
                    if (!mounted || !user) return;
                    const fresh = user as { id?: unknown; legal?: LegalState };
                    // The server's clock decides when it refuses, not this
                    // browser's: a record that still looks as if there were
                    // time is read as due. Only one with nothing open (the
                    // user accepted in another tab meanwhile) keeps the app.
                    const next =
                        gateState(fresh.legal, Date.now()) === 'ok'
                            ? 'ok'
                            : 'blocked';
                    // Only for the user this decision is about: a record of
                    // anyone else says nothing here.
                    setDecision(current =>
                        current && current.userId === String(fresh.id)
                            ? { ...current, state: next, refused: true }
                            : current
                    );
                })
                .catch(error => {
                    console.error('Could not re-read the user record:', error);
                });
        });
        // Callables have no shared client to hook into. Most of them run
        // inside a query or a mutation, so their errors pass here: `failed`
        // after every refused attempt, `error` when the retries are used up
        // (about seven seconds later with the defaults). Waiting for the
        // second would leave the user that long in front of empty pages.
        const watch = (event: CacheEvent) => {
            if (event.type !== 'updated') return;
            if (
                event.action?.type === 'failed' ||
                event.action?.type === 'error'
            ) {
                reportIfLegalRequired(event.action.error);
            }
        };
        const offQueries = queryClient.getQueryCache().subscribe(watch);
        const offMutations = queryClient.getMutationCache().subscribe(watch);
        return () => {
            mounted = false;
            offRefusal();
            offQueries();
            offMutations();
        };
    }, [queryClient]);

    if (state === null) return null;

    // Both slots keep their position in every state. The app therefore stays
    // mounted when the dialog closes over it, and the open dialog stays
    // mounted when the app appears or is taken away beneath it.
    return (
        <>
            {state !== 'blocked' && <>{children}</>}
            {state !== 'ok' && identity && (
                // The lazy part can fail to arrive, typically right after a
                // deployment replaced the files. That must not take the app
                // down for a user who may still work, nor let a user in who
                // has to decide first.
                <ErrorBoundary
                    fallbackRender={() =>
                        state === 'blocked' ? <LoadFailed /> : null
                    }
                >
                    <Suspense
                        fallback={state === 'blocked' ? <LegalWaiting /> : null}
                    >
                        <LegalFlow
                            blocked={state === 'blocked'}
                            refused={refused}
                            userId={String(identity.id)}
                            legal={legal}
                            onState={onState}
                        />
                    </Suspense>
                </ErrorBoundary>
            )}
        </>
    );
};

const LoadFailed = () => (
    <Box
        role="alert"
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mt: 12,
            px: 2,
            textAlign: 'center',
        }}
    >
        <Typography>
            The legal documents could not be loaded. Reload the page to try
            again.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
        </Button>
    </Box>
);
